// App - top-level router: auth/bootstrap/change-password gates + lazy admin chunks.

import { lazy, Suspense, useDeferredValue, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router';
import AppLayout from '@/components/AppLayout';
import { Spinner } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/classnames';
import { documentTitle } from '@/lib/useDocumentTitle';
import AcceptInvitePage from '@/pages/AcceptInvitePage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import FirstUserRegistrationPage from '@/pages/FirstUserRegistrationPage';
// Critical-path pages - eager so the first/only screen never flashes a
// spinner on initial load.
import LoginPage from '@/pages/LoginPage';
import ProfilePage from '@/pages/ProfilePage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SubmissionsPage from '@/pages/SubmissionsPage';
import SubmitPage from '@/pages/SubmitPage';

// Heavy pages lazy-loaded (~50% smaller initial JS). ReportPage alone pulls
// in xyflow + dagre + recharts (>200KB); admin pages are is_admin-gated.
const ReportPage = lazy(() => import('@/pages/ReportPage'));
// The URL console carries its own chrome, its telemetry panel and a JSON
// explorer, none of which a file submission ever loads.
const SubmitUrlPage = lazy(() => import('@/pages/SubmitUrlPage'));
// Standalone, shell-less operator surface opened in its own tab by ConsoleButton.
const ConsolePage = lazy(() => import('@/pages/ConsolePage'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminMachinesPage = lazy(() => import('@/pages/admin/AdminMachinesPage'));
const AdminMachineDetailPage = lazy(() => import('@/pages/admin/AdminMachineDetailPage'));
const AdminOrgsPage = lazy(() => import('@/pages/admin/AdminOrgsPage'));
const AdminOrgDetailPage = lazy(() => import('@/pages/admin/AdminOrgDetailPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'));
const AdminProvidersPage = lazy(() => import('@/pages/admin/AdminProvidersPage'));
const AdminProviderDetailPage = lazy(() => import('@/pages/admin/AdminProviderDetailPage'));
const AdminPaworkersPage = lazy(() => import('@/pages/admin/AdminPaworkersPage'));
const AdminPaworkerDetailPage = lazy(() => import('@/pages/admin/AdminPaworkerDetailPage'));
const AdminPostAnalysisStepsPage = lazy(() => import('@/pages/admin/AdminPostAnalysisStepsPage'));
const AdminPostAnalysisJobsPage = lazy(() => import('@/pages/admin/AdminPostAnalysisJobsPage'));
const OrgSettingsLayout = lazy(() => import('@/pages/org/OrgSettingsLayout'));
const OrgAPIKeysPage = lazy(() => import('@/pages/org/OrgAPIKeysPage'));
const DocsLayout = lazy(() => import('@/pages/docs/DocsLayout'));
const DocsOverviewPage = lazy(() => import('@/pages/docs/start/OverviewPage'));
const DocsQuickstartPage = lazy(() => import('@/pages/docs/start/QuickstartPage'));
const DocsAuthenticationPage = lazy(() => import('@/pages/docs/start/AuthenticationPage'));
const DocsPythonSDKPage = lazy(() => import('@/pages/docs/start/PythonSDKPage'));
const DocsRESTPage = lazy(() => import('@/pages/docs/start/RESTPage'));
const DocsGuidePage = lazy(() => import('@/pages/docs/guides/GuidePage'));
const DocsConnectAgentPage = lazy(() => import('@/pages/docs/agents/ConnectAgentPage'));
const DocsTriageAgentPage = lazy(() => import('@/pages/docs/agents/TriageAgentPage'));
const DocsProtocolPage = lazy(() => import('@/pages/docs/agents/ProtocolPage'));
const DocsMcpConnectPage = lazy(() => import('@/pages/docs/agents/McpConnectPage'));
const DocsMcpTriagePage = lazy(() => import('@/pages/docs/agents/McpTriagePage'));
const DocsMcpToolsPage = lazy(() => import('@/pages/docs/agents/McpToolsPage'));
const DocsReferenceOverviewPage = lazy(() => import('@/pages/docs/reference/ReferenceOverviewPage'));
const DocsOutputPage = lazy(() => import('@/pages/docs/reference/OutputPage'));
const DocsErrorsPage = lazy(() => import('@/pages/docs/reference/ErrorsPage'));
const DocsTagPage = lazy(() => import('@/pages/docs/reference/TagPage'));
const DocsOperatorOverviewPage = lazy(() => import('@/pages/docs/reference/OperatorOverviewPage'));
const DocsOperatorTagPage = lazy(() => import('@/pages/docs/reference/OperatorTagPage'));
const OrgUsagePage = lazy(() => import('@/pages/org/OrgUsagePage'));
const OrgTeamPage = lazy(() => import('@/pages/org/OrgTeamPage'));
const OrgSettingsPage = lazy(() => import('@/pages/org/OrgSettingsPage'));
const AdminBaseImagesPage = lazy(() => import('@/pages/admin/AdminBaseImagesPage'));
const AdminAnalyzersPage = lazy(() => import('@/pages/admin/AdminAnalyzersPage'));
const AdminEgressPage = lazy(() => import('@/pages/admin/AdminEgressPage'));
const AdminAnalyzerDetailPage = lazy(() => import('@/pages/admin/AdminAnalyzerDetailPage'));
const AdminAuditPage = lazy(() => import('@/pages/admin/AdminAuditPage'));
const AdminSystemPage = lazy(() => import('@/pages/admin/AdminSystemPage'));
const CalculatorPage = lazy(() => import('@/pages/admin/CalculatorPage'));
const AdminPlatformKeysPage = lazy(() => import('@/pages/admin/AdminPlatformKeysPage'));
const SetupWizard = lazy(() => import('@/pages/SetupWizard'));


function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <Spinner size={20} />
    </div>
  );
}

// Slim indeterminate top bar shown while a navigation is held waiting on a
// lazy route chunk. We keep the previous page on screen during that wait
// (useDeferredValue below), so this is the only signal that a click landed;
// it fades so a sub-frame, fully-cached navigation never flashes it on.
//
// The bar stays mounted so it can fade, and an infinite animation under an
// invisible parent still runs, keeping the compositor busy and firing an
// animationiteration event every cycle on an idle tab. So the sweep is paused
// whenever no navigation is held, which also freezes it in place for the
// fade-out rather than snapping it away. Reduced motion is handled by the
// global guard in index.css, which cuts every animation to one near-instant
// iteration.
function RouteProgress({ active }: { active: boolean }) {
  return (
    <div
      aria-hidden
      className={cn(
        'fixed inset-x-0 top-0 z-[60] h-0.5 transition-opacity duration-200',
        active ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div className="relative h-full w-full overflow-hidden bg-accent-500/15">
        <div
          className={cn(
            'absolute inset-y-0 left-0 w-1/3 bg-accent-400 animate-route-progress',
            !active && '[animation-play-state:paused]',
          )}
        />
      </div>
    </div>
  );
}

// What the tab is called, per route.
//
// Keyed on the first path segment, which is the section a reader is actually
// looking for; a deeper page that has a better name than its section sets its
// own with useDocumentTitle. Lives beside the route table so a new route is
// one edit away from a title rather than silently inheriting the last one.
//
// Rendered AHEAD of <Routes> so a page's own title effect runs after this one
// and overrides it - the ordering is the whole mechanism, so keep it first.
const SECTION_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  submissions: 'Submissions',
  submit: 'Submit a sample',
  'submit-url': 'Submit a URL',
  profile: 'Profile',
  org: 'Organization',
  admin: 'Admin',
  console: 'Console',
  docs: 'Docs',
  setup: 'Setup',
  login: 'Sign in',
  'accept-invite': 'Accept invitation',
  'reset-password': 'Reset password',
  'change-password': 'Change password',
  'register-first-user': 'Create the first account',
};

export function RouteTitle({ pathname }: { pathname: string }) {
  // The label is derived INSIDE the effect, on the path, because the path is
  // what has to re-run it. Two paths in one section share a label, and the
  // deeper one may have overwritten the title on its way past: leaving a
  // report for the list it came from is the ordinary case, and a label-keyed
  // effect would not fire, so the list would stay named after the report. Not
  // fixed by a cleanup in useDocumentTitle either - the outgoing page's
  // cleanup runs after this effect and would clobber the destination.
  useEffect(() => {
    document.title = documentTitle(SECTION_TITLES[pathname.split('/')[1] ?? ''] ?? null);
  }, [pathname]);
  return null;
}

// Submitting a URL was a tab on the sample page until it grew a console of its
// own. Links to that tab are in bookmarks and in older reports, so the tab it
// names still resolves - to the page that took the job over.
function SubmitRoute() {
  const [params] = useSearchParams();
  if (params.get('tab') === 'url') return <Navigate to="/submit-url" replace />;
  return <SubmitPage />;
}

// Warms the lazy() admin chunks for admin users. AdminLayout +
// AdminProvidersPage go first so the initial /admin click doesn't pay two
// sequential Suspense fallbacks; the rest fetch under requestIdleCallback.
function prefetchAdminChunks() {
  const idle: (cb: () => void) => void =
    typeof window !== 'undefined' && 'requestIdleCallback' in window
      ? (cb) =>
          (window as unknown as { requestIdleCallback: (c: () => void) => void })
            .requestIdleCallback(cb)
      : (cb) => setTimeout(cb, 200);

  // Critical pair - fired immediately so they race the user's first /admin click.
  void import('@/pages/admin/AdminLayout');
  void import('@/pages/admin/AdminProvidersPage');

  // Secondary tabs under idle.
  idle(() => { void import('@/pages/admin/AdminMachinesPage'); });
  idle(() => { void import('@/pages/admin/AdminMachineDetailPage'); });
  idle(() => { void import('@/pages/admin/AdminBaseImagesPage'); });
  idle(() => { void import('@/pages/admin/AdminAnalyzersPage'); });
  idle(() => { void import('@/pages/admin/AdminEgressPage'); });
  idle(() => { void import('@/pages/admin/AdminOrgsPage'); });
  idle(() => { void import('@/pages/admin/AdminOrgDetailPage'); });
  idle(() => { void import('@/pages/admin/AdminUsersPage'); });
  idle(() => { void import('@/pages/admin/AdminAuditPage'); });
  idle(() => { void import('@/pages/admin/AdminSystemPage'); });
  idle(() => { void import('@/pages/admin/AdminProviderDetailPage'); });
  idle(() => { void import('@/pages/admin/AdminPaworkersPage'); });
  idle(() => { void import('@/pages/admin/AdminPaworkerDetailPage'); });
  idle(() => { void import('@/pages/admin/AdminPostAnalysisStepsPage'); });
  idle(() => { void import('@/pages/admin/AdminPostAnalysisJobsPage'); });
  idle(() => { void import('@/pages/SetupWizard'); });
}

export default function App() {
  const { user, loading, needsBootstrap } = useAuth();
  const location = useLocation();

  // Route content is matched against a *deferred* copy of the location, so a
  // navigation to a not-yet-loaded lazy chunk keeps the current page rendered
  // until the next one is ready, then swaps - instead of unmounting it for a
  // Suspense fallback. The nav highlight (which reads the real location) is
  // unaffected, and `isNavPending` drives the top progress bar during the wait.
  const deferredLocation = useDeferredValue(location);
  const isNavPending = deferredLocation !== location;

  // Prefetch admin chunks once auth resolves to admin; Vite dedupes the
  // later React.lazy imports against this.
  useEffect(() => {
    if (user?.is_admin) {
      prefetchAdminChunks();
    }
  }, [user?.is_admin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <Spinner size={20} />
      </div>
    );
  }

  // First-time setup: no admin yet. Force the registration page so the
  // operator can't bounce off /login against an empty user table.
  if (!user && needsBootstrap) {
    return (
      <>
        <RouteTitle pathname={location.pathname} />
        <Routes>
          <Route path="/register-first-user" element={<FirstUserRegistrationPage />} />
          <Route path="*" element={<Navigate to="/register-first-user" replace />} />
        </Routes>
      </>
    );
  }

  // must_change_password operators are intercepted SPA-side for a clean
  // redirect; the gateway would otherwise 423-block every other endpoint.
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return (
      <>
        <RouteTitle pathname={location.pathname} />
        <Routes>
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="*" element={<Navigate to="/change-password" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <RouteTitle pathname={deferredLocation.pathname} />
      <RouteProgress active={isNavPending} />
      <Suspense fallback={<RouteFallback />}>
      <Routes location={deferredLocation}>
        {/* Public auth pages */}
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes */}
        {user ? (
          <>
          {/* Standalone, shell-less console tab (admin only) - sibling of the
              app shell so it fills the window with no rail/topbar.
              Note: user.is_admin is a display gate only. Security enforcement
              is server-side: every /api/admin/* route requires admin auth. */}
          {user.is_admin && <Route path="/console" element={<ConsolePage />} />}
          {user.is_admin && <Route path="/setup" element={<SetupWizard />} />}
          {/* The documentation site, also shell-less. It is read for long
              stretches and deep-linked into from outside, so it owns the
              window: its own header, section tabs, rail and page index, with
              a link back to the dashboard. The structure lives in
              pages/docs/nav.ts; addresses from earlier shapes of the docs
              are forwarded by the layout, which is why the catch-all is here. */}
          <Route path="/docs" element={<DocsLayout />}>
            <Route index element={<DocsOverviewPage />} />
            <Route path="quickstart" element={<DocsQuickstartPage />} />
            <Route path="authentication" element={<DocsAuthenticationPage />} />
            <Route path="python-sdk" element={<DocsPythonSDKPage />} />
            <Route path="rest" element={<DocsRESTPage />} />
            <Route path="guides/:slug" element={<DocsGuidePage />} />
            <Route path="agents" element={<DocsConnectAgentPage />} />
            <Route path="agents/triage-agent" element={<DocsTriageAgentPage />} />
            <Route path="agents/protocol" element={<DocsProtocolPage />} />
            <Route path="agents/mcp" element={<DocsMcpConnectPage />} />
            <Route path="agents/mcp/triage" element={<DocsMcpTriagePage />} />
            <Route path="agents/mcp/tools" element={<DocsMcpToolsPage />} />
            <Route path="reference" element={<DocsReferenceOverviewPage />} />
            <Route path="reference/output" element={<DocsOutputPage />} />
            <Route path="reference/errors" element={<DocsErrorsPage />} />
            <Route path="reference/:tag" element={<DocsTagPage />} />
            <Route path="operator" element={<DocsOperatorOverviewPage />} />
            <Route path="operator/:tag" element={<DocsOperatorTagPage />} />
            <Route path="*" element={<Navigate to="/docs" replace />} />
          </Route>
          <Route element={<AppLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={
              user.is_admin && user.setup_required ? <Navigate to="/setup" replace /> : <DashboardPage />
            } />
            {/* A link from before the URL console had its own page. */}
            <Route path="/submit" element={<SubmitRoute />} />
            <Route path="/submit-url" element={<SubmitUrlPage />} />
            <Route path="/submissions" element={<SubmissionsPage />} />
            {/* The report keeps the shell (rail + topbar) but renders as a
                branded light document inside it - see brand-scope in
                index.css and ReportFrame in ReportPage. */}
            <Route path="/submissions/:id" element={<ReportPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/org/:orgId/settings" element={<OrgSettingsLayout />}>
              <Route index element={<Navigate to="api-keys" replace />} />
              <Route path="api-keys" element={<OrgAPIKeysPage />} />
              <Route path="usage" element={<OrgUsagePage />} />
              <Route path="team" element={<OrgTeamPage />} />
              <Route path="settings" element={<OrgSettingsPage />} />
            </Route>
            {/* Note: user.is_admin is a display gate only - see /console comment above. */}
            {user.is_admin && (
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/providers" replace />} />
                <Route path="machines" element={<AdminMachinesPage />} />
                <Route path="machines/:machineId" element={<AdminMachineDetailPage />} />
                <Route path="orgs" element={<AdminOrgsPage />} />
                <Route path="orgs/:orgId" element={<AdminOrgDetailPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="providers" element={<AdminProvidersPage />} />
                <Route path="providers/:providerId" element={<AdminProviderDetailPage />} />
                <Route path="paworkers" element={<AdminPaworkersPage />} />
                <Route path="paworkers/:paworkerId" element={<AdminPaworkerDetailPage />} />
                <Route path="post-analysis/steps" element={<AdminPostAnalysisStepsPage />} />
                <Route path="post-analysis/jobs" element={<AdminPostAnalysisJobsPage />} />
                <Route path="base-images" element={<AdminBaseImagesPage />} />
                <Route path="analyzers" element={<AdminAnalyzersPage />} />
                <Route path="analyzers/:name" element={<AdminAnalyzerDetailPage />} />
                <Route path="egress" element={<AdminEgressPage />} />

                <Route path="system" element={<AdminSystemPage />} />
                <Route path="calculator" element={<CalculatorPage />} />
                <Route path="platform-keys" element={<AdminPlatformKeysPage />} />
                {/* Legacy /admin/orchestration bookmarks → /admin/providers;
                    deep links drop to the list (Navigate's `to=` is static). */}
                <Route path="orchestration" element={<Navigate to="/admin/providers" replace />} />
                <Route path="orchestration/:providerId" element={<Navigate to="/admin/providers" replace />} />
                <Route path="specs" element={<Navigate to="/admin/providers" replace />} />
                <Route path="config" element={<Navigate to="/admin/providers" replace />} />
                <Route path="audit" element={<AdminAuditPage />} />
              </Route>
            )}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
          </>
        ) : (
          <Route path="*" element={<Navigate to="/login" replace />} />
        )}
      </Routes>
      </Suspense>
    </>
  );
}
